import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Button,
  Tooltip,
  Collapse,
  Alert,
  LinearProgress,
  Fab,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  PostAdd as PostIcon,
  Autorenew as AutoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  syncSquarePayments,
  subscribePayments,
  unsubscribePayments,
  postSinglePayment,
  enableAutoPosting,
  disableAutoPosting,
  checkAutoPostingStatus,
} from '../slices/payments';
import ErrorBoundary from './ErrorBoundary';
import errorReporter from '../utils/errorReporting';
import { canopy } from '../theme';
// import { formatDistanceToNow } from 'date-fns'; // Will add this dependency later

// Simple time ago formatter (temporary until date-fns is added)
const formatTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const statusPill = (status, posted) => {
  if (posted) {
    return { label: 'POSTED', bg: canopy.soft, color: canopy.greenDark, Icon: CheckCircleIcon };
  }
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      return { label: 'READY TO POST', bg: canopy.warnSoft, color: canopy.warn, Icon: ScheduleIcon };
    case 'APPROVED':
      return { label: 'PROCESSING', bg: canopy.surface, color: canopy.inkSoft, Icon: ScheduleIcon };
    case 'PENDING':
      return { label: 'PENDING', bg: canopy.surface, color: canopy.inkSoft, Icon: ScheduleIcon };
    default:
      return {
        label: (status || 'UNKNOWN').toUpperCase(),
        bg: canopy.dangerSoft,
        color: canopy.danger,
        Icon: ErrorIcon,
      };
  }
};

const StatusPill = ({ status, posted }) => {
  const pill = statusPill(status, posted);
  const { Icon } = pill;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        borderRadius: 999,
        bgcolor: pill.bg,
        color: pill.color,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon sx={{ fontSize: 14 }} />
      {pill.label}
    </Box>
  );
};

const customerNameOf = (payment) =>
  payment.firstName && payment.lastName
    ? `${payment.firstName} ${payment.lastName}`
    : payment.firstName || payment.lastName || 'Unknown Customer';

const PaymentDetails = ({ payment, createdAt }) => (
  <Grid container spacing={2}>
    {payment.cardDetails && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>
          Card details
        </Typography>
        <Typography variant="body2">
          <strong>Brand:</strong> {payment.cardDetails.card?.cardBrand}
        </Typography>
        <Typography variant="body2">
          <strong>Last 4:</strong> **** {payment.cardDetails.card?.last4}
        </Typography>
        <Typography variant="body2">
          <strong>Expires:</strong> {payment.cardDetails.card?.expMonth}/{payment.cardDetails.card?.expYear}
        </Typography>
        <Typography variant="body2">
          <strong>Status:</strong> {payment.cardDetails.status}
        </Typography>
      </Grid>
    )}

    <Grid item xs={12} md={6}>
      <Typography variant="subtitle2" gutterBottom>
        Transaction timeline
      </Typography>
      <Typography variant="body2">
        <strong>Created:</strong> {createdAt.toLocaleString()}
      </Typography>
      {payment.cardDetails?.cardPaymentTimeline?.capturedAt && (
        <Typography variant="body2">
          <strong>Captured:</strong> {new Date(payment.cardDetails.cardPaymentTimeline.capturedAt).toLocaleString()}
        </Typography>
      )}
      {payment.processingFee?.[0] && (
        <Typography variant="body2">
          <strong>Processing fee:</strong> ${(payment.processingFee[0].amountMoney.amount / 100).toFixed(2)}
        </Typography>
      )}
    </Grid>

    <Grid item xs={12}>
      <Typography variant="subtitle2" gutterBottom>
        Customer information
      </Typography>
      <Typography variant="body2">
        <strong>Customer ID:</strong> {payment.customerId}
      </Typography>
      <Typography variant="body2">
        <strong>Currency:</strong> {payment.currency || 'USD'}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        <strong>Transaction:</strong> {payment.id}
      </Typography>
    </Grid>
  </Grid>
);

const PostAction = ({ payment, onPost, autoPostingEnabled }) => {
  if (payment.posted || payment.status !== 'COMPLETED') return null;
  return (
    <Tooltip title={autoPostingEnabled ? 'Auto-posting will handle this transaction' : ''}>
      <span>
        <Button
          variant="contained"
          size="small"
          startIcon={<PostIcon />}
          onClick={() => onPost(payment.id)}
          disabled={autoPostingEnabled}
        >
          Post
        </Button>
      </span>
    </Tooltip>
  );
};

// Mobile layout: one card per payment
const PaymentCard = ({ payment, onExpand, expanded, onPost, autoPostingEnabled }) => {
  const amount = payment.totalMoney?.amount ? (payment.totalMoney.amount / 100).toFixed(2) : '0.00';
  const createdAt = payment.createdAt ? new Date(payment.createdAt) : new Date();
  const customerName = customerNameOf(payment);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                bgcolor: canopy.soft,
                color: canopy.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {payment.cardDetails?.card?.cardBrand ? <CreditCardIcon /> : <BankIcon />}
            </Box>
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 800 }}>
                ${amount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {customerName}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <StatusPill status={payment.status} posted={payment.posted} />
            {payment.receiptUrl && (
              <Tooltip title="View Receipt">
                <IconButton size="small" onClick={() => window.open(payment.receiptUrl, '_blank')}>
                  <ReceiptIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {formatTimeAgo(createdAt)} · Receipt #{payment.receiptNumber || 'N/A'}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="small"
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => onExpand(payment.id)}
          >
            {expanded ? 'Less details' : 'More details'}
          </Button>
          <PostAction payment={payment} onPost={onPost} autoPostingEnabled={autoPostingEnabled} />
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${canopy.hairline}` }}>
            <PaymentDetails payment={payment} createdAt={createdAt} />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Desktop layout: table rows with an expandable detail panel
const PaymentTable = ({ payments, expandedCard, onExpand, onPost, autoPostingEnabled }) => (
  <Card sx={{ overflow: 'hidden' }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.6fr 1.2fr 1.2fr 1.4fr auto',
        gap: 2,
        px: 3.5,
        py: 2,
        bgcolor: canopy.surface,
        fontSize: 13,
        fontWeight: 800,
        color: canopy.inkSoft,
      }}
    >
      <Box>Amount</Box>
      <Box>Customer</Box>
      <Box>Card</Box>
      <Box>Date</Box>
      <Box>Status</Box>
      <Box sx={{ width: 150, textAlign: 'right' }}>Actions</Box>
    </Box>
    {payments.map((payment) => {
      const amount = payment.totalMoney?.amount ? (payment.totalMoney.amount / 100).toFixed(2) : '0.00';
      const createdAt = payment.createdAt ? new Date(payment.createdAt) : new Date();
      const card = payment.cardDetails?.card;
      const expanded = expandedCard === payment.id;
      return (
        <Box key={payment.id} sx={{ borderBottom: `1px solid ${canopy.hairline}`, '&:last-of-type': { borderBottom: 'none' } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1.6fr 1.2fr 1.2fr 1.4fr auto',
              gap: 2,
              px: 3.5,
              py: 1.75,
              alignItems: 'center',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            <Box sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>${amount}</Box>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {customerNameOf(payment)}
            </Box>
            <Box sx={{ color: 'text.secondary' }}>
              {card ? `${card.cardBrand} ·· ${card.last4}` : '—'}
            </Box>
            <Box sx={{ color: 'text.secondary' }}>{formatTimeAgo(createdAt)}</Box>
            <Box>
              <StatusPill status={payment.status} posted={payment.posted} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, width: 150 }}>
              <PostAction payment={payment} onPost={onPost} autoPostingEnabled={autoPostingEnabled} />
              {payment.receiptUrl && (
                <Tooltip title="View Receipt">
                  <IconButton size="small" onClick={() => window.open(payment.receiptUrl, '_blank')}>
                    <ReceiptIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => onExpand(expanded ? null : payment.id)}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={expanded}>
            <Box sx={{ px: 3.5, py: 2, bgcolor: canopy.surface }}>
              <PaymentDetails payment={payment} createdAt={createdAt} />
            </Box>
          </Collapse>
        </Box>
      );
    })}
    {payments.length === 0 && (
      <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary', fontWeight: 600 }}>
        No payment transactions found.
      </Box>
    )}
  </Card>
);

const PaymentsHeader = ({ payments, autoPostingEnabled, onToggleAutoPosting, onRefresh, syncing }) => {
  const summary = useMemo(() => {
    const paymentsArray = Object.values(payments || {});
    const pending = paymentsArray.filter(
      (p) => p.status === 'COMPLETED' && !p.posted,
    ).length;
    return { total: paymentsArray.length, pending };
  }, [payments]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" component="h1">
            Payments
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
            {summary.total} transactions · {summary.pending} awaiting posting
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Tooltip title="Toggle Auto-Posting">
            <Button
              variant={autoPostingEnabled ? 'contained' : 'outlined'}
              startIcon={<AutoIcon />}
              onClick={onToggleAutoPosting}
              color={autoPostingEnabled ? 'success' : 'primary'}
            >
              Auto-Post
            </Button>
          </Tooltip>

          <Tooltip title="Pull the latest payments from Square">
            <span>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                disabled={syncing}
              >
                {syncing ? 'Syncing…' : 'Refresh'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

const StatTile = ({ label, value, sub, dotColor }) => (
  <Card sx={{ p: 2.5, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      {dotColor && (
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1, color: 'text.primary' }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
        {sub}
      </Typography>
    )}
  </Card>
);

const PaymentStatsSummary = ({ payments }) => {
  const stats = useMemo(() => {
    const paymentsArray = Object.values(payments || {});

    const totalTransactions = paymentsArray.length;
    const completedTransactions = paymentsArray.filter(p => p.status === 'COMPLETED').length;
    const postedTransactions = paymentsArray.filter(p => p.posted).length;
    const pendingPosting = completedTransactions - postedTransactions;

    const totalAmount = paymentsArray.reduce((sum, p) => sum + (p.totalMoney?.amount || 0), 0);
    const postedAmount = paymentsArray
      .filter(p => p.posted)
      .reduce((sum, p) => sum + (p.totalMoney?.amount || 0), 0);
    const pendingAmount = paymentsArray
      .filter(p => p.status === 'COMPLETED' && !p.posted)
      .reduce((sum, p) => sum + (p.totalMoney?.amount || 0), 0);

    return {
      totalTransactions,
      completedTransactions,
      postedTransactions,
      pendingPosting,
      totalAmount: (totalAmount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
      postedAmount: (postedAmount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
      pendingAmount: (pendingAmount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
    };
  }, [payments]);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} md={3}>
        <StatTile label="Total transactions" value={stats.totalTransactions} sub={stats.totalAmount} />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatTile
          label="Awaiting posting"
          value={stats.pendingPosting}
          sub={stats.pendingAmount}
          dotColor={canopy.warn}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatTile
          label="Posted"
          value={stats.postedTransactions}
          sub={stats.postedAmount}
          dotColor={canopy.green}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatTile label="Completed" value={stats.completedTransactions} sub="Ready for posting" />
      </Grid>
    </Grid>
  );
};

const PaymentsModern = () => {
  const dispatch = useDispatch();
  const { payments, loading, syncing, error, autoPostingEnabled } = useSelector((state) => state.payments);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    try {
      // Live feeds for the table; a Square sync in the background pulls in
      // anything new, which the feeds then deliver.
      dispatch(subscribePayments());
      dispatch(syncSquarePayments());
      dispatch(checkAutoPostingStatus());
      return () => dispatch(unsubscribePayments());
    } catch (error) {
      errorReporter.reportUIError(error, 'PaymentsModern', 'initial_load');
    }
  }, [dispatch]);

  const handleRefresh = useCallback(() => dispatch(syncSquarePayments()), [dispatch]);

  const filteredPayments = useMemo(() => {
    const paymentsArray = Object.entries(payments || {}).map(([key, payment]) => ({
      ...payment,
      id: key,
      // Ensure consistent data structure
      amount: payment.totalMoney?.amount || 0,
      currency: payment.totalMoney?.currency || 'USD',
      name: customerNameOf(payment),
    }));

    return paymentsArray
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [payments]);

  const handlePostPayment = useCallback(async (transactionId) => {
    try {
      await dispatch(postSinglePayment(transactionId));
    } catch (error) {
      errorReporter.reportPaymentError(error, 'manual_post', {
        transaction_id: transactionId
      });
    }
  }, [dispatch]);

  const handleToggleAutoPosting = useCallback(() => {
    if (autoPostingEnabled) {
      dispatch(disableAutoPosting());
    } else {
      dispatch(enableAutoPosting());
    }
  }, [autoPostingEnabled, dispatch]);

  if (loading && !Object.keys(payments || {}).length) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading payment transactions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payment transactions: {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            dispatch(subscribePayments());
            dispatch(syncSquarePayments());
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <ErrorBoundary component="PaymentsModern">
      <Box sx={{ p: 3, pb: 12, minHeight: '100vh', bgcolor: 'background.default', maxWidth: 1100, mx: 'auto' }}>
        <PaymentsHeader
          onRefresh={handleRefresh}
          syncing={syncing}
          payments={payments}
          autoPostingEnabled={autoPostingEnabled}
          onToggleAutoPosting={handleToggleAutoPosting}
        />

        <PaymentStatsSummary payments={payments} />

        {autoPostingEnabled && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<AutoIcon />}>
            <strong>Auto-posting is enabled.</strong> Completed transactions will be posted automatically every 5 minutes.
            Manual posting is disabled while auto-posting is active.
          </Alert>
        )}

        {/* Desktop table */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <PaymentTable
            payments={filteredPayments}
            expandedCard={expandedCard}
            onExpand={setExpandedCard}
            onPost={handlePostPayment}
            autoPostingEnabled={autoPostingEnabled}
          />
        </Box>

        {/* Mobile cards */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {filteredPayments.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No payment transactions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment transactions will appear here when customers make payments.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            filteredPayments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                expanded={expandedCard === payment.id}
                onExpand={(id) => setExpandedCard(expandedCard === id ? null : id)}
                onPost={handlePostPayment}
                autoPostingEnabled={autoPostingEnabled}
              />
            ))
          )}
        </Box>

        {loading && (
          <Box sx={{ position: 'fixed', bottom: 24, right: 24 }}>
            <Fab color="primary" disabled>
              <RefreshIcon />
            </Fab>
          </Box>
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default PaymentsModern;
